import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import React, { useContext, useEffect, useState, useRef } from 'react';
import { constants } from '../../constants';
import useMyMApi from "../../hooks/useMyMApi";
import useWebContext from '../../hooks/useWebContext';
import { AuthUserContext } from '../../provider/AuthUserContextProvider';
import { MyMButton } from '../../ui-elements/MyMButton';
import { MyMVariantLabel } from '../../ui-elements/MyMLabel';
import { MyMLink, MyMBlackLink } from '../../ui-elements/MyMLink';
import { MyMModal } from '../../ui-elements/MyMModal';
import { MyMProgress } from '../../ui-elements/MyMProgress';
import { MyMTooltip } from '../../ui-elements/MyMToolTip';
import { NavigateLink } from '../../ui-parts/NavigateLink';
import SubThemePage from '../../ui-parts/SubThemePage';
import { UploadRequest } from '../MyM205_UploadRequest/UploadRequest';
import UserProfileDetail from './UserProfileDetail';
import UserProfileFavoriteFile from './UserProfileFavoriteFile';
import UserProfileFavoriteItApps from './UserProfileFavoriteItApps';
import { mediaQuery, useMediaQuery } from '../../hooks/useMediaQuery';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import { utility } from '../../utility';

/**
 * UserProfile Page Component
 */
const UserProfile = () => {
  // 変数・State
  const screenId = 'MyM202';
  const apiRequest = useMyMApi(screenId);
  const { controlSettings, messageList, screenSettings } = useWebContext(screenId);
  const [userInfo, setUserInfo] = useState(null);
  const [favoriteBoxFileInfo, setFavoriteBoxFileInfo] = useState({});
  const [favoriteItApp, setFavoriteItApp] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitLoaded, setIsInitLoaded] = useState(false);
  const [favoritePageNum, setFavoritePageNum] = useState(1);
  const [linkMenuAnchor, setLinkMenuAnchor] = useState(null);
  const openLinkMenu = Boolean(linkMenuAnchor);
  const isPc = useMediaQuery(mediaQuery.pc);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const tooltipRef = useRef(null);

  // ページロード時処理
  useEffect(() => {
    // APIコールバック処理
    const callbackPageLoadContext = (result) => {
      setUserInfo({ ...result.UserInfo });
      setFavoriteBoxFileInfo({ ...result.FavoriteBoxFileInfo });
      setFavoriteItApp([...result.FavoriteItAppList]);
    }

    // UserInfoController.csのAPI呼び出し
    // User情報、お気に入り(ファイル/ITApps)を取得する
    apiRequest.requestJson('api/user-info/user-profile', 'GET', callbackPageLoadContext);
    setIsInitLoaded(true);
  }, []);

  /** ページング切り替え時 */
  useEffect(() => {
    if (isInitLoaded === false) { return; }
    getFavoriteFilePage(favoritePageNum);
  }, [favoritePageNum]);

  /**
   * Favorite Fileページ情報取得
   * @param {Number} pageNum ページ番号
   */
  const getFavoriteFilePage = (pageNum) => {
    if (isInitLoaded === false) { return; }
    setIsLoading(true);

    const callbackSuccess = (result) => {

      // ページ番号が2以上で検索結果が無い場合、前のページで再検索
      if (pageNum > 1 && result.ItemList.length === 0) {
        setFavoritePageNum(pageNum - 1);
        return;
      }

      setFavoriteBoxFileInfo(result);
      setIsLoading(false);
    }
    const callbackFailed = () => setIsLoading(false);

    // 指定されたページ範囲のファイルを取得する
    apiRequest.requestJson(`api/box-file/get-favorite-page?LimitMaxRowCount=${screenSettings.FavoriteBoxFileDispCount}&PageNumber=${pageNum}`, 'GET', callbackSuccess, callbackFailed)
  };

  // 2024.5.8 MyMine Version1.5対応 Add Start
  const { authUser, setAuthUser } = useContext(AuthUserContext);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const handleModalClose = () => setIsModalOpen(false);
  const onClickUploadReqest = (e) => {
    setIsModalOpen(true);
  }
  const isUploadRequestFlag = !(authUser?.RoleId === constants.UserRole.Administrators || authUser?.RoleId === constants.UserRole.Uploaders);
  // 2024.5.8 MyMine Version1.5対応 Add End

  /** Managementメニューボタン押下時処理 */
  const onClickManagementMenu = (e) => {
    setLinkMenuAnchor(e.currentTarget);
  };

  /** ManagementメニューClose処理 */
  const closeManagementMenu = () => {
    setLinkMenuAnchor(null);
  }

  const handleTooltipToggle = () => {
    setIsTooltipVisible(!isTooltipVisible);
  };

  const handleClickOutside = (event) => {
    if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
      setIsTooltipVisible(false);
    }
  };

  /** ログアウト処理 */
  const onClickLogout = () => {
    setAuthUser(null);
    utility.transferTopPage();
  }

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  //
  const modalWidth = isPc ? '61%' : '90%';
  const modalHeight = isPc ? '37%' : '60%';

  // Managementメニュー情報
  let managementLinkMenuInfo = null;
  switch (userInfo?.RoleId) {
    case constants.UserRole.Administrators: // Role: Administrators
      managementLinkMenuInfo = {
        menuButtonText: controlSettings.LabelList.btnAdminFunction,
        linkItemList: [
          { to: '/MyM203', linkText: controlSettings.LabelList.lnkUserList },
          { to: '/MyM503', linkText: controlSettings.LabelList.lnkNewsManagement },
          { to: '/MyM601', linkText: controlSettings.LabelList.lnkFileManagement },
          // 2024.9.16 MyMine Version2.0対応 Add Start
          { to: '/MyM206', linkText: controlSettings.LabelList.lnkEmailDelivery },
          { to: '/MyM803', linkText: controlSettings.LabelList.lnkSurveyManagement },
          { to: '/MyM208', linkText: controlSettings.LabelList.lnkNotificationManagement },
          // 2024.9.16 MyMine Version2.0対応 Add End
        ]
      };
      break;

    case constants.UserRole.Uploaders: // Role: Uploaders
      managementLinkMenuInfo = {
        menuButtonText: controlSettings.LabelList.btnUploaderFunction,
        linkItemList: [
          { to: '/MyM601', linkText: controlSettings.LabelList.lnkFileManagement },
        ]
      };
      break;

    default:
      break;
  }

  if (userInfo === null)
    return <MyMProgress />

  return (
    <SubThemePage>
      {isLoading && <MyMProgress />}
      {isPc
        ? (
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }} >
            <MyMVariantLabel variant='h5' sx={{ color: "#fff", fontWeight: 'bold', textAlign: 'center', paddingTop: '10px', flexGrow: 1 }}>{controlSettings.LabelList.lblTitle}</MyMVariantLabel>
            <MyMBlackLink onClick={onClickLogout} sx={{ color: 'white', paddingTop: '25px', paddingRight: '20px', fontSize: 17 }}>{controlSettings.LabelList.lnkLogout}</MyMBlackLink>
          </Box>
      ) : (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }} >
              <MyMVariantLabel variant='h5' sx={{ color: "#fff", fontWeight: 'bold', textAlign: 'center', paddingTop: '10px', flexGrow: 1 }}>{controlSettings.LabelList.lblTitle}</MyMVariantLabel>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <MyMBlackLink onClick={onClickLogout} sx={{ color: 'white', paddingTop: '5px', fontSize: 17 }}>{controlSettings.LabelList.lnkLogout}</MyMBlackLink>
            </Box>
          </Box>
        )}
      <Grid
        container
        spacing={1}
        className="mt-0 py-1"
        sx={{
          paddingRight: "1vw",
          '.profile-content': {
            border: 1, borderColor: '#fff', borderRadius: '10px', padding: '0 1vw 0 1vw',
            maxHeight: isUploadRequestFlag ? '620px' : '590px',
            minHeight: isUploadRequestFlag ? '620px' : '590px',
            '& > div': {
              height: isUploadRequestFlag ? "610px" : "580px",
            }
          },
          '.profile-content-body': {
            border: 1, borderColor: '#fff', height: isUploadRequestFlag ? "520px" : "495px",
          }
        }}>
        { /* User Detail Area */}
        <UserProfileDetail
          apiRequest={apiRequest}
          controlSettings={controlSettings}
          messageList={messageList}
          userInfo={userInfo}
          setUserInfo={setUserInfo}
          setIsLoading={setIsLoading} />
        { /* MyFavorite Files Area */}
        <UserProfileFavoriteFile
          screenId={screenId}
          apiRequest={apiRequest}
          controlSettings={controlSettings}
          messageList={messageList}
          favoriteBoxFileInfo={favoriteBoxFileInfo}
          setIsLoading={setIsLoading}
          favoritePageNum={favoritePageNum}
          setFavoritePageNum={setFavoritePageNum}
          getFavoriteFilePage={getFavoriteFilePage} />
        { /* MyFavorite IT Apps Area */}
        <UserProfileFavoriteItApps
          screenId={screenId}
          apiRequest={apiRequest}
          controlSettings={controlSettings}
          favoriteItApp={favoriteItApp}
          setFavoriteItApp={setFavoriteItApp}
          setIsLoading={setIsLoading} />
      </Grid>

      {managementLinkMenuInfo &&
        <Box>
          <MyMButton className='my-2' onClick={onClickManagementMenu}>{managementLinkMenuInfo.menuButtonText}</MyMButton>
          <Menu
            anchorEl={linkMenuAnchor}
            open={openLinkMenu}
            onClose={closeManagementMenu}
            anchorOrigin={{ vertical: 'top', horizontal: 'left', }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'left', }}
            PaperProps={{
              sx: { backgroundColor: '#fff' }
            }}>
            {managementLinkMenuInfo.linkItemList.map((x, i) =>
              <MenuItem key={i}>
                <NavigateLink navigateCallback={closeManagementMenu} to={x.to} newTabNavigate={true} isBlackTheme={true}>{x.linkText}</NavigateLink>
              </MenuItem>
            )}
          </Menu>
        </Box>
      }
      {/*  2024.5.8 MyMine Version1.5対応 Add Start*/}
      <>
        {isUploadRequestFlag && (
          <Box display="flex" alignItems="center">
            <MyMTooltip title={controlSettings.LabelList.lblUploadRequestToolTip}>
              <MyMLink underline="hover" onClick={onClickUploadReqest} sx={{ color: "#fff" }}>
                {controlSettings.LabelList.lnkUploadRequest}
              </MyMLink>
            </MyMTooltip>

            {!isPc && (
              <div ref={tooltipRef}>
                <MyMTooltip
                  title={controlSettings.LabelList.lblUploadRequestToolTip}
                  open={isTooltipVisible}
                >
                  <Box
                    component="span"
                    sx={{ cursor: 'pointer', marginLeft: '10px' }}
                    onClick={handleTooltipToggle}
                  >
                    <HelpOutlineOutlinedIcon sx={{
                      color: 'white',
                      fontSize: '24px', 
                    }} />
                  </Box>
                </MyMTooltip>
              </div>
            )}
          </Box>
        )}

      </>

      <MyMModal
        open={isModalOpen}
        onClose={handleModalClose}
        width={modalWidth}
        height={modalHeight}
      >
        <UploadRequest handleModalClose={handleModalClose}></UploadRequest>
      </MyMModal>
      {/*  2024.5.8 MyMine Version1.5対応 Add End*/}
    </SubThemePage>
  );
}
export default UserProfile;
